import React from 'react';
import { Link } from 'react-router-dom';
import { theme, responsiveContainer, createGradient } from '../styles/theme';

function Features() {
  const handleCardHover = (e, isHovering) => {
    // Find the actual card element (not child elements)
    const card = e.currentTarget;
    const glow = card.querySelector('.card-glow');
    
    if (isHovering) {
      card.style.transform = 'translateY(-8px)';
      card.style.boxShadow = theme.shadows.xl;
      if (glow) glow.style.opacity = '1';
    } else {
      card.style.transform = 'translateY(0)';
      card.style.boxShadow = theme.shadows.md;
      if (glow) glow.style.opacity = '0';
    }
  };

  const styles = {
    container: {
      backgroundColor: theme.colors.background,
      fontFamily: "'Inter', sans-serif",
      minHeight: '100vh',
    },
    
    // Hero Section
    hero: {
      background: createGradient(theme.colors.primary, theme.colors.primaryDark, '135deg').background,
      color: '#fff',
      padding: '120px 20px 80px 20px',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
    },
    
    heroPattern: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: 0.1,
      background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Crect x='11' y='11' width='4' height='4'/%3E%3Crect x='51' y='11' width='4' height='4'/%3E%3Crect x='11' y='51' width='4' height='4'/%3E%3Crect x='51' y='51' width='4' height='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    },
    
    heroContent: {
      ...responsiveContainer,
      position: 'relative',
      zIndex: 1,
    },
    
    heroTitle: {
      fontSize: theme.typography.fontSizes['6xl'],
      fontWeight: theme.typography.fontWeights.extrabold,
      marginBottom: theme.spacing.lg,
      lineHeight: '1.1',
      textShadow: '0 2px 4px rgba(0,0,0,0.1)',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    
    heroSubtitle: {
      fontSize: theme.typography.fontSizes['2xl'],
      fontWeight: theme.typography.fontWeights.normal,
      marginBottom: theme.spacing['3xl'],
      maxWidth: '800px',
      margin: `0 auto ${theme.spacing['3xl']} auto`,
      lineHeight: '1.6',
      opacity: 0.95,
      fontFamily: "'Inter', sans-serif",
    },

    // Main Features Section
    features: {
      backgroundColor: theme.colors.surface,
      padding: '100px 20px',
    },
    
    featuresContent: {
      ...responsiveContainer,
    },
    
    sectionTitle: {
      fontSize: theme.typography.fontSizes['4xl'],
      fontWeight: theme.typography.fontWeights.bold,
      color: theme.colors.text,
      textAlign: 'center',
      marginBottom: theme.spacing['3xl'],
      position: 'relative',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    
    sectionTitleUnderline: {
      content: '""',
      position: 'absolute',
      bottom: '-10px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '80px',
      height: '4px',
      backgroundColor: theme.colors.primary,
      borderRadius: theme.borderRadius.full,
    },
    
    featuresGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
      gap: theme.spacing['2xl'],
      marginTop: theme.spacing['2xl'],
    },
    
    featureCard: {
      backgroundColor: theme.colors.background,
      borderRadius: theme.borderRadius.xl,
      padding: theme.spacing['2xl'],
      border: `2px solid ${theme.colors.borderDark}`,
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: theme.shadows.md,
      cursor: 'pointer',
    },

    featureCardGlow: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(135deg, ${theme.colors.primary}10, transparent)`,
      opacity: 0,
      transition: 'opacity 0.4s ease',
    },
    
    featureIcon: {
      fontSize: theme.typography.fontSizes['4xl'],
      marginBottom: theme.spacing.lg,
      color: theme.colors.primary,
      fontWeight: theme.typography.fontWeights.bold,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      textShadow: `0 0 20px ${theme.colors.primary}40`,
      display: 'block',
    },
    
    featureTitle: {
      fontSize: theme.typography.fontSizes['2xl'],
      fontWeight: theme.typography.fontWeights.bold,
      color: theme.colors.text,
      marginBottom: theme.spacing.md,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    
    featureDescription: {
      fontSize: theme.typography.fontSizes.base,
      color: theme.colors.textLight,
      lineHeight: '1.6',
      marginBottom: theme.spacing.lg,
      fontFamily: "'Inter', sans-serif",
    },

    featureHighlights: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },

    featureHighlight: {
      display: 'flex',
      alignItems: 'flex-start',
      marginBottom: theme.spacing.md,
      fontSize: theme.typography.fontSizes.sm,
      color: theme.colors.text,
      fontFamily: "'Inter', sans-serif",
    },

    highlightIcon: {
      color: theme.colors.primary,
      marginRight: theme.spacing.sm,
      fontWeight: theme.typography.fontWeights.bold,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      fontSize: theme.typography.fontSizes.base,
      minWidth: '20px',
    },

    // ByteBot Builder Demo Section
    builderDemo: {
      backgroundColor: theme.colors.background,
      padding: '100px 20px',
      position: 'relative',
    },

    builderContent: {
      ...responsiveContainer,
      textAlign: 'center',
    },

    demoContainer: {
      maxWidth: '900px',
      margin: `${theme.spacing['2xl']} auto 0 auto`,
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.xl,
      overflow: 'hidden',
      boxShadow: theme.shadows.xl,
      border: `2px solid ${theme.colors.borderDark}`,
    },

    demoHeader: {
      backgroundColor: theme.colors.primary,
      color: '#fff',
      padding: theme.spacing.lg,
      textAlign: 'left',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      fontSize: theme.typography.fontSizes.sm,
      fontWeight: theme.typography.fontWeights.bold,
    },

    chatInterface: {
      backgroundColor: theme.colors.background,
      padding: theme.spacing['2xl'],
      minHeight: '400px',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.md,
    },

    chatMessage: {
      padding: theme.spacing.md,
      borderRadius: theme.borderRadius.md,
      maxWidth: '80%',
      fontFamily: "'Inter', sans-serif",
      fontSize: theme.typography.fontSizes.sm,
      lineHeight: '1.5',
    },

    assistantMessage: {
      backgroundColor: `${theme.colors.primary}15`,
      border: `1px solid ${theme.colors.primary}30`,
      color: theme.colors.text,
      alignSelf: 'flex-start',
      textAlign: 'left',
    },

    userMessage: {
      backgroundColor: theme.colors.primary,
      color: '#fff',
      alignSelf: 'flex-end',
      textAlign: 'right',
    },

    chatInput: {
      marginTop: 'auto',
      display: 'flex',
      gap: theme.spacing.sm,
      padding: theme.spacing.md,
      borderTop: `1px solid ${theme.colors.borderDark}`,
      backgroundColor: theme.colors.surface,
    },

    inputField: {
      flex: 1,
      padding: theme.spacing.md,
      border: `1px solid ${theme.colors.borderDark}`,
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.background,
      color: theme.colors.text,
      fontFamily: "'Inter', sans-serif",
      fontSize: theme.typography.fontSizes.sm,
    },

    sendButton: {
      padding: `${theme.spacing.md} ${theme.spacing.lg}`,
      backgroundColor: theme.colors.primary,
      color: '#fff',
      border: 'none',
      borderRadius: theme.borderRadius.md,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      fontSize: theme.typography.fontSizes.sm,
      cursor: 'default',
      fontWeight: theme.typography.fontWeights.bold,
      pointerEvents: 'none',
    },

    // Widget Showcase Section
    widgetShowcase: {
      backgroundColor: theme.colors.surface,
      padding: '100px 20px',
    },

    widgetContent: {
      ...responsiveContainer,
      textAlign: 'center',
    },

    widgetDemo: {
      position: 'relative',
      maxWidth: '900px',
      margin: `${theme.spacing['2xl']} auto 0 auto`,
      backgroundColor: '#f0f0f0',
      borderRadius: '8px',
      padding: '0',
      overflow: 'hidden',
      boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
    },

    browserFrame: {
      backgroundColor: '#e8e8e8',
      padding: '12px 16px',
      borderBottom: '1px solid #d0d0d0',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },

    browserDots: {
      display: 'flex',
      gap: '6px',
    },

    browserDot: {
      width: '12px',
      height: '12px',
      borderRadius: '50%',
    },

    redDot: {
      backgroundColor: '#ff5f57',
    },

    yellowDot: {
      backgroundColor: '#ffbd2e',
    },

    greenDot: {
      backgroundColor: '#28ca42',
    },

    addressBar: {
      flex: 1,
      marginLeft: '20px',
      backgroundColor: '#fff',
      border: '1px solid #d0d0d0',
      borderRadius: '4px',
      padding: '4px 12px',
      fontSize: '13px',
      color: '#666',
    },

    websitePreview: {
      backgroundColor: '#fff',
      padding: '0',
      textAlign: 'left',
      height: '750px',
      position: 'relative',
      overflow: 'hidden',
    },

    chatWidget: {
      position: 'absolute',
      bottom: '20px',
      right: '20px',
      width: '350px',
      height: '450px',
      backgroundColor: '#fff',
      border: `2px solid #D4AF37`,
      borderRadius: '12px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      fontFamily: "'Inter', sans-serif",
    },

    widgetHeader: {
      backgroundColor: '#1a1a1a',
      color: '#fff',
      padding: '15px',
      fontWeight: '600',
      fontSize: '14px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: '1px solid #D4AF37',
      flexShrink: 0,
    },

    widgetMessages: {
      flex: 1,
      padding: '15px',
      backgroundColor: '#f8f9fa',
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },

    message: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    },

    messageUser: {
      alignSelf: 'flex-end',
      maxWidth: '80%',
    },

    messageBot: {
      alignSelf: 'flex-start',
      maxWidth: '80%',
    },

    messageBubble: {
      padding: '10px 14px',
      borderRadius: '18px',
      fontSize: '14px',
      lineHeight: '1.4',
      wordWrap: 'break-word',
    },

    messageBubbleUser: {
      backgroundColor: '#D4AF37',
      color: '#fff',
      borderBottomRightRadius: '6px',
      textAlign: 'right',
    },

    messageBubbleBot: {
      backgroundColor: '#fff',
      color: '#333',
      border: '1px solid #e0e0e0',
      borderBottomLeftRadius: '6px',
      textAlign: 'left',
    },

    messageTime: {
      fontSize: '11px',
      color: '#888',
      marginTop: '2px',
    },

    messageTimeUser: {
      textAlign: 'right',
    },

    messageTimeBot: {
      textAlign: 'left',
    },

    widgetInputArea: {
      padding: '15px',
      borderTop: '1px solid #e0e0e0',
      backgroundColor: '#fff',
      display: 'flex',
      gap: '10px',
      alignItems: 'center',
      flexShrink: 0,
    },

    widgetInput: {
      flex: 1,
      padding: '10px 14px',
      border: '1px solid #e0e0e0',
      borderRadius: '20px',
      fontSize: '14px',
      outline: 'none',
      backgroundColor: '#f8f9fa',
    },

    widgetSendBtn: {
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      backgroundColor: '#D4AF37',
      border: 'none',
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      fontSize: '16px',
      flexShrink: 0,
    },

    // Story Integration Section
    storySection: {
      backgroundColor: theme.colors.background,
      padding: '100px 20px',
    },

    storyContent: {
      ...responsiveContainer,
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
      gap: theme.spacing['4xl'],
      alignItems: 'center',
    },

    storyText: {
      fontSize: theme.typography.fontSizes.lg,
      lineHeight: '1.7',
      color: theme.colors.text,
      fontFamily: "'Inter', sans-serif",
    },

    highlight: {
      backgroundColor: theme.colors.primary,
      color: '#fff',
      padding: theme.spacing['3xl'],
      borderRadius: theme.borderRadius.xl,
      border: `2px solid ${theme.colors.primary}`,
      position: 'relative',
      overflow: 'hidden',
      boxShadow: theme.shadows.xl,
    },
    
    highlightPattern: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: 0.1,
      background: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Cpath d='M20 20c0 4.4-3.6 8-8 8s-8-3.6-8-8 3.6-8 8-8 8 3.6 8 8zm0-20c0 4.4-3.6 8-8 8s-8-3.6-8-8 3.6-8 8-8 8 3.6 8 8z'/%3E%3C/g%3E%3C/svg%3E")`,
    },
    
    highlightText: {
      fontSize: theme.typography.fontSizes['2xl'],
      fontWeight: theme.typography.fontWeights.semibold,
      margin: 0,
      lineHeight: '1.5',
      position: 'relative',
      zIndex: 1,
      fontFamily: "'Inter', sans-serif",
      fontStyle: 'italic',
    },

    // Process Section
    process: {
      background: createGradient('#1a1a1a', theme.colors.secondary, '135deg').background,
      color: '#fff',
      padding: '120px 20px',
      position: 'relative',
      overflow: 'hidden',
    },

    processOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23007acc' fill-opacity='0.05'%3E%3Cpath d='M0 0h80v80H0V0zm20 20v40h40V20H20zm20 35a15 15 0 1 1 0-30 15 15 0 0 1 0 30z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      opacity: 0.3,
    },
    
    processContent: {
      ...responsiveContainer,
      position: 'relative',
      zIndex: 1,
      textAlign: 'center',
    },

    processSubtitle: {
      fontSize: theme.typography.fontSizes['2xl'],
      fontWeight: theme.typography.fontWeights.normal,
      marginBottom: theme.spacing['3xl'],
      maxWidth: '800px',
      margin: `0 auto ${theme.spacing['3xl']} auto`,
      lineHeight: '1.6',
      opacity: 0.95,
      fontFamily: "'Inter', sans-serif",
    },

    processGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: theme.spacing['2xl'],
      marginTop: theme.spacing['2xl'],
    },

    processStep: {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      border: `2px solid rgba(255, 255, 255, 0.2)`,
      borderRadius: theme.borderRadius.xl,
      padding: theme.spacing['2xl'],
      textAlign: 'center',
      transition: 'all 0.4s ease',
      position: 'relative',
      overflow: 'hidden',
      backdropFilter: 'blur(10px)',
    },

    stepNumber: {
      fontSize: theme.typography.fontSizes['4xl'],
      fontWeight: theme.typography.fontWeights.extrabold,
      color: theme.colors.primary,
      marginBottom: theme.spacing.md,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      textShadow: `0 0 20px ${theme.colors.primary}60`,
    },

    stepTitle: {
      fontSize: theme.typography.fontSizes.xl,
      fontWeight: theme.typography.fontWeights.bold,
      color: '#fff',
      marginBottom: theme.spacing.md,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },

    stepDescription: {
      color: 'rgba(255, 255, 255, 0.9)',
      lineHeight: '1.6',
      fontSize: theme.typography.fontSizes.base,
      fontFamily: "'Inter', sans-serif",
    },

    // CTA Section
    cta: {
      backgroundColor: theme.colors.surface,
      padding: '100px 20px',
      textAlign: 'center',
    },

    ctaContent: {
      ...responsiveContainer,
    },

    ctaTitle: {
      fontSize: theme.typography.fontSizes['4xl'],
      fontWeight: theme.typography.fontWeights.bold,
      color: theme.colors.text,
      marginBottom: theme.spacing.lg,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },

    ctaDescription: {
      fontSize: theme.typography.fontSizes.xl,
      color: theme.colors.textLight,
      marginBottom: theme.spacing['2xl'],
      maxWidth: '600px',
      margin: `0 auto ${theme.spacing['2xl']} auto`,
      opacity: 0.9,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },

    ctaButton: {
      backgroundColor: theme.colors.primary,
      color: '#fff',
      padding: `${theme.spacing.lg} ${theme.spacing['2xl']}`,
      borderRadius: theme.borderRadius.lg,
      border: 'none',
      fontSize: theme.typography.fontSizes.lg,
      fontWeight: theme.typography.fontWeights.semibold,
      textDecoration: 'none',
      display: 'inline-block',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      boxShadow: theme.shadows.lg,
    },

    // Responsive styles are handled by CSS grid auto-fit
  };

  return (
    <div style={styles.container}>
      {/* Hero Section */}
      <section style={styles.hero}>
        <div style={styles.heroPattern}></div>
        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle}>Features & Capabilities</h1>
          <p style={styles.heroSubtitle}>
            Powerful tools for intelligent customer experiences. From conversational setup to AI analysis.
          </p>
        </div>
      </section>

      {/* Core Features Section */}
      <section style={styles.features}>
        <div style={styles.featuresContent}>
          <h2 style={styles.sectionTitle}>
            Core Features
            <div style={styles.sectionTitleUnderline}></div>
          </h2>
          
          <div style={styles.featuresGrid}>
            {/* ByteBot Builder */}
            <div 
              style={styles.featureCard}
              onMouseEnter={(e) => handleCardHover(e, true)}
              onMouseLeave={(e) => handleCardHover(e, false)}
            >
              <div className="card-glow" style={styles.featureCardGlow}></div>
              <div style={styles.featureIcon}>[BUILDER]</div>
              <h3 style={styles.featureTitle}>ByteBot Builder</h3>
              <p style={styles.featureDescription}>
                Create your perfect chatbot through natural conversation with our AI assistant. No coding, no complex interfaces - just tell ByteBot Builder what you need and watch your chatbot come to life.
              </p>
              <ul style={styles.featureHighlights}>
                <li style={styles.featureHighlight}>
                  <span style={styles.highlightIcon}>&gt;</span>
                  <span>Conversational setup process</span>
                </li>
                <li style={styles.featureHighlight}>
                  <span style={styles.highlightIcon}>&gt;</span>
                  <span>Plain English configuration</span>
                </li>
                <li style={styles.featureHighlight}>
                  <span style={styles.highlightIcon}>&gt;</span>
                  <span>Instant preview and testing</span>
                </li>
                <li style={styles.featureHighlight}>
                  <span style={styles.highlightIcon}>&gt;</span>
                  <span>Custom personality and tone</span>
                </li>
              </ul>
            </div>

            {/* Smart Knowledge Base */}
            <div 
              style={styles.featureCard}
              onMouseEnter={(e) => handleCardHover(e, true)}
              onMouseLeave={(e) => handleCardHover(e, false)}
            >
              <div className="card-glow" style={styles.featureCardGlow}></div>
              <div style={styles.featureIcon}>[KB]</div>
              <h3 style={styles.featureTitle}>Smart Knowledge Base</h3>
              <p style={styles.featureDescription}>
                Build your chatbot's intelligence with text, FAQs, and soon - direct file uploads. Your bot learns from your content to provide accurate, contextual responses.
              </p>
              <ul style={styles.featureHighlights}>
                <li style={styles.featureHighlight}>
                  <span style={styles.highlightIcon}>&gt;</span>
                  <span>Text and FAQ integration</span>
                </li>
                <li style={styles.featureHighlight}>
                  <span style={styles.highlightIcon}>&gt;</span>
                  <span>File upload support (coming soon)</span>
                </li>
                <li style={styles.featureHighlight}>
                  <span style={styles.highlightIcon}>&gt;</span>
                  <span>Automatic content processing</span>
                </li>
                <li style={styles.featureHighlight}>
                  <span style={styles.highlightIcon}>&gt;</span>
                  <span>Contextual understanding</span>
                </li>
              </ul>
            </div>

            {/* AI Analysis Tool */}
            <div 
              style={styles.featureCard}
              onMouseEnter={(e) => handleCardHover(e, true)}
              onMouseLeave={(e) => handleCardHover(e, false)}
            >
              <div className="card-glow" style={styles.featureCardGlow}></div>
              <div style={styles.featureIcon}>[AI]</div>
              <h3 style={styles.featureTitle}>AI Analysis Tool</h3>
              <p style={styles.featureDescription}>
                Get intelligent insights into your chatbot's performance. Our AI analyzes conversations, scores interactions, and provides actionable recommendations for improvement.
              </p>
              <ul style={styles.featureHighlights}>
                <li style={styles.featureHighlight}>
                  <span style={styles.highlightIcon}>&gt;</span>
                  <span>Performance scoring system</span>
                </li>
                <li style={styles.featureHighlight}>
                  <span style={styles.highlightIcon}>&gt;</span>
                  <span>Conversation analysis</span>
                </li>
                <li style={styles.featureHighlight}>
                  <span style={styles.highlightIcon}>&gt;</span>
                  <span>Improvement recommendations</span>
                </li>
                <li style={styles.featureHighlight}>
                  <span style={styles.highlightIcon}>&gt;</span>
                  <span>Usage pattern insights</span>
                </li>
              </ul>
            </div>

            {/* Effortless Setup */}
            <div 
              style={styles.featureCard}
              onMouseEnter={(e) => handleCardHover(e, true)}
              onMouseLeave={(e) => handleCardHover(e, false)}
            >
              <div className="card-glow" style={styles.featureCardGlow}></div>
              <div style={styles.featureIcon}>[DEPLOY]</div>
              <h3 style={styles.featureTitle}>Effortless Setup</h3>
              <p style={styles.featureDescription}>
                From concept to deployment in minutes, not months. Our streamlined process gets your chatbot live and helping customers faster than any traditional solution.
              </p>
              <ul style={styles.featureHighlights}>
                <li style={styles.featureHighlight}>
                  <span style={styles.highlightIcon}>&gt;</span>
                  <span>5-minute setup process</span>
                </li>
                <li style={styles.featureHighlight}>
                  <span style={styles.highlightIcon}>&gt;</span>
                  <span>Multiple deployment options</span>
                </li>
                <li style={styles.featureHighlight}>
                  <span style={styles.highlightIcon}>&gt;</span>
                  <span>Real-time performance monitoring</span>
                </li>
                <li style={styles.featureHighlight}>
                  <span style={styles.highlightIcon}>&gt;</span>
                  <span>No technical expertise required</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ByteBot Builder Demo Section */}
      <section style={styles.builderDemo}>
        <div style={styles.builderContent}>
          <h2 style={styles.sectionTitle}>ByteBot Builder In Action</h2>
          <p style={styles.featureDescription}>
            Watch how easy it is to create a chatbot with our conversational ByteBot Builder. This is an example of the actual interface 
            you'll use in your dashboard - no complex forms or technical setup required. Just have a natural conversation 
            and watch as ByteBot Builder automatically saves information to your knowledge base.
          </p>
          
          <div style={styles.demoContainer}>
                         <div style={styles.demoHeader}>
               [BYTEBOT BUILDER] - Restaurant Chatbot Creation
             </div>
            <div style={styles.chatInterface}>
              <div style={{...styles.chatMessage, ...styles.assistantMessage}}>
                Hi! I'm your ByteBot Builder. I'll help you create a chatbot for your business. What type of business do you run?
              </div>
              
              <div style={{...styles.chatMessage, ...styles.userMessage}}>
                I run a small Italian restaurant called "Bella Vista" and need help with reservations and menu questions.
              </div>
              
              <div style={{...styles.chatMessage, ...styles.assistantMessage}}>
                Perfect! An Italian restaurant chatbot. Let me gather some details. What are your most popular dishes that customers ask about?
              </div>
              
              <div style={{...styles.chatMessage, ...styles.userMessage}}>
                Our signature pasta dishes, wood-fired pizzas, and tiramisu are very popular. We also have daily specials.
              </div>
              
                             <div style={{...styles.chatMessage, ...styles.assistantMessage}}>
                 Excellent! I'm adding this to your knowledge base. What hours are you open, and how do customers typically make reservations?
               </div>
               
               <div style={{...styles.chatMessage, fontSize: '12px', color: theme.colors.primary, fontStyle: 'italic', alignSelf: 'center', backgroundColor: 'transparent', border: 'none', padding: '4px'}}>
                 ✓ Saved to knowledge base: Popular dishes and specialties
               </div>
               
               <div style={{...styles.chatMessage, ...styles.userMessage}}>
                 We're open Tuesday-Sunday, 5-10pm. Customers can call us or use OpenTable.
               </div>
               
               <div style={{...styles.chatMessage, fontSize: '12px', color: theme.colors.primary, fontStyle: 'italic', alignSelf: 'center', backgroundColor: 'transparent', border: 'none', padding: '4px'}}>
                 ✓ Saved to knowledge base: Hours and reservation methods
               </div>
               
               <div style={{...styles.chatMessage, ...styles.assistantMessage}}>
                 Perfect! I've created your restaurant chatbot with menu information, hours, and reservation details. Your bot is ready to help customers with common questions. Would you like to test it now?
               </div>
              
              <div style={styles.chatInput}>
                <input 
                  style={styles.inputField} 
                  placeholder="Type your message here..." 
                  disabled 
                />
                <button style={styles.sendButton}>[SEND]</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Widget Showcase Section */}
      <section style={styles.widgetShowcase}>
        <div style={styles.widgetContent}>
                     <h2 style={styles.sectionTitle}>Your Chat Widget</h2>
           <p style={styles.featureDescription}>
             Your chatbot comes with a beautiful, customizable chat widget that easily embeds into any website. 
             Match your brand colors, customize the greeting message, choose the perfect size and position, 
             and create a seamless customer experience that feels native to your site.
           </p>
           <div style={{
             display: 'grid',
             gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
             gap: theme.spacing.lg,
             margin: `${theme.spacing.lg} 0 ${theme.spacing['2xl']} 0`,
             textAlign: 'left'
           }}>
             <div>
               <h4 style={{color: theme.colors.primary, marginBottom: theme.spacing.sm, fontFamily: "'Courier New', monospace"}}>
                 [CUSTOMIZABLE]
               </h4>
               <p style={{fontSize: theme.typography.fontSizes.sm, color: theme.colors.textLight, margin: 0}}>
                 Colors, fonts, size, and position all match your website's theme
               </p>
             </div>
             <div>
               <h4 style={{color: theme.colors.primary, marginBottom: theme.spacing.sm, fontFamily: "'Courier New', monospace"}}>
                 [EASY EMBED]
               </h4>
               <p style={{fontSize: theme.typography.fontSizes.sm, color: theme.colors.textLight, margin: 0}}>
                 One line of code adds the widget to any website or platform
               </p>
             </div>
             <div>
               <h4 style={{color: theme.colors.primary, marginBottom: theme.spacing.sm, fontFamily: "'Courier New', monospace"}}>
                 [RESPONSIVE]
               </h4>
               <p style={{fontSize: theme.typography.fontSizes.sm, color: theme.colors.textLight, margin: 0}}>
                 Works perfectly on desktop, tablet, and mobile devices
               </p>
             </div>
           </div>
          
          <div style={styles.widgetDemo}>
            {/* Browser Frame */}
            <div style={styles.browserFrame}>
              <div style={styles.browserDots}>
                <div style={{...styles.browserDot, ...styles.redDot}}></div>
                <div style={{...styles.browserDot, ...styles.yellowDot}}></div>
                <div style={{...styles.browserDot, ...styles.greenDot}}></div>
              </div>
              <div style={styles.addressBar}>
                https://bellavistany.com
              </div>
            </div>
            
            <div style={styles.websitePreview}>
               {/* Header */}
               <div style={{
                 backgroundColor: '#1a1a1a',
                 color: '#fff',
                 padding: '12px 0',
                 borderBottom: '1px solid #333'
               }}>
                 <div style={{
                   maxWidth: '1200px',
                   margin: '0 auto',
                   padding: '0 20px',
                   display: 'flex',
                   justifyContent: 'space-between',
                   alignItems: 'center'
                 }}>
                   <div style={{
                     fontSize: '28px',
                     fontWeight: 'bold',
                     fontFamily: "'Playfair Display', serif",
                     color: '#D4AF37'
                   }}>
                     Bella Vista
                   </div>
                   <nav style={{display: 'flex', gap: '30px', fontSize: '15px', fontWeight: '500'}}>
                     <span style={{color: '#D4AF37', borderBottom: '2px solid #D4AF37', paddingBottom: '2px'}}>Home</span>
                     <span style={{color: '#ccc', cursor: 'pointer'}}>Menu</span>
                     <span style={{color: '#ccc', cursor: 'pointer'}}>Reservations</span>
                     <span style={{color: '#ccc', cursor: 'pointer'}}>About</span>
                     <span style={{color: '#ccc', cursor: 'pointer'}}>Contact</span>
                   </nav>
                 </div>
               </div>
               
               {/* Hero Section with Background Image */}
               <div style={{
                 backgroundImage: 'linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url("https://images.unsplash.com/photo-1555396273-367ea4eb4db5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80")',
                 backgroundSize: 'cover',
                 backgroundPosition: 'center',
                 color: '#fff',
                 padding: '80px 20px',
                 textAlign: 'center',
                 position: 'relative'
               }}>
                 <div style={{maxWidth: '600px', margin: '0 auto'}}>
                   <h1 style={{
                     margin: '0 0 20px 0', 
                     fontSize: '48px', 
                     fontFamily: "'Playfair Display', serif",
                     fontWeight: '700',
                     lineHeight: '1.2',
                     textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                   }}>
                     Authentic Italian Experience
                   </h1>
                   <p style={{
                     margin: '0 0 30px 0', 
                     fontSize: '20px', 
                     opacity: 0.95,
                     lineHeight: '1.6',
                     fontWeight: '300',
                     textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
                   }}>
                     Where tradition meets passion. Handcrafted pasta, wood-fired pizzas, and the finest Italian wines in an intimate setting.
                   </p>
                   <div style={{
                     display: 'inline-block',
                     backgroundColor: '#D4AF37',
                     color: '#1a1a1a',
                     padding: '15px 35px',
                     fontSize: '16px',
                     fontWeight: '600',
                     borderRadius: '0',
                     textTransform: 'uppercase',
                     letterSpacing: '1px',
                     boxShadow: '0 4px 15px rgba(212, 175, 55, 0.3)'
                   }}>
                     Reserve Your Table
                   </div>
                 </div>
               </div>
               
               {/* Content Section */}
               <div style={{backgroundColor: '#fafafa', padding: '50px 20px'}}>
                 <div style={{maxWidth: '1200px', margin: '0 auto'}}>
                   {/* Featured Dishes */}
                   <div style={{textAlign: 'center', marginBottom: '40px'}}>
                     <h2 style={{
                       fontSize: '36px',
                       fontFamily: "'Playfair Display', serif",
                       color: '#1a1a1a',
                       marginBottom: '15px',
                       fontWeight: '600'
                     }}>
                       Signature Dishes
                     </h2>
                     <div style={{
                       width: '60px',
                       height: '3px',
                       backgroundColor: '#D4AF37',
                       margin: '0 auto 30px auto'
                     }}></div>
                   </div>
                   
                   <div style={{
                     display: 'grid', 
                     gridTemplateColumns: 'repeat(3, 1fr)', 
                     gap: '30px',
                     marginBottom: '50px'
                   }}>
                     <div style={{textAlign: 'center'}}>
                       <div style={{
                         width: '100%',
                         height: '200px',
                         backgroundImage: 'url("https://images.unsplash.com/photo-1551183053-bf91a1d81141?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80")',
                         backgroundSize: 'cover',
                         backgroundPosition: 'center',
                         borderRadius: '8px',
                         marginBottom: '15px'
                       }}></div>
                       <h3 style={{
                         fontSize: '20px',
                         fontFamily: "'Playfair Display', serif",
                         color: '#1a1a1a',
                         marginBottom: '8px'
                       }}>
                         Handmade Pasta
                       </h3>
                       <p style={{fontSize: '14px', color: '#666', lineHeight: '1.5'}}>
                         Fresh pasta made daily with traditional techniques passed down through generations.
                       </p>
                     </div>
                     
                     <div style={{textAlign: 'center'}}>
                       <div style={{
                         width: '100%',
                         height: '200px',
                         backgroundImage: 'url("https://images.unsplash.com/photo-1513104890138-7c749659a591?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80")',
                         backgroundSize: 'cover',
                         backgroundPosition: 'center',
                         borderRadius: '8px',
                         marginBottom: '15px'
                       }}></div>
                       <h3 style={{
                         fontSize: '20px',
                         fontFamily: "'Playfair Display', serif",
                         color: '#1a1a1a',
                         marginBottom: '8px'
                       }}>
                         Wood-Fired Pizza
                       </h3>
                       <p style={{fontSize: '14px', color: '#666', lineHeight: '1.5'}}>
                         Authentic Neapolitan pizzas baked in our traditional wood-fired oven at 900°F.
                       </p>
                     </div>
                     
                     <div style={{textAlign: 'center'}}>
                       <div style={{
                         width: '100%',
                         height: '200px',
                         backgroundImage: 'url("https://images.unsplash.com/photo-1571877227200-a0d98ea607e9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80")',
                         backgroundSize: 'cover',
                         backgroundPosition: 'center',
                         borderRadius: '8px',
                         marginBottom: '15px'
                       }}></div>
                       <h3 style={{
                         fontSize: '20px',
                         fontFamily: "'Playfair Display', serif",
                         color: '#1a1a1a',
                         marginBottom: '8px'
                       }}>
                         Classic Tiramisu
                       </h3>
                       <p style={{fontSize: '14px', color: '#666', lineHeight: '1.5'}}>
                         Our signature dessert made with imported mascarpone and the finest espresso.
                       </p>
                     </div>
                   </div>
                   
                   {/* Restaurant Info - Cut off section */}
                   <div style={{
                     backgroundColor: '#fff',
                     padding: '40px',
                     borderRadius: '12px',
                     boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
                     display: 'grid',
                     gridTemplateColumns: '2fr 1fr',
                     gap: '40px',
                     alignItems: 'center',
                     marginTop: '30px'
                   }}>
                     <div>
                       <h3 style={{
                         fontSize: '28px',
                         fontFamily: "'Playfair Display', serif",
                         color: '#1a1a1a',
                         marginBottom: '20px'
                       }}>
                         Experience Italy in Downtown
                       </h3>
                       <p style={{
                         fontSize: '16px',
                         color: '#555',
                         lineHeight: '1.7',
                         marginBottom: '20px'
                       }}>
                         Step into Bella Vista and be transported to the heart of Italy. Our intimate dining room features exposed brick walls, warm lighting, and the aroma of fresh herbs and garlic that fills the air.
                       </p>
                       <p style={{
                         fontSize: '16px',
                         color: '#555',
                         lineHeight: '1.7'
                       }}>
                         Chef Marco brings 25 years of experience from his family's restaurant in Tuscany, ensuring every dish tells a story of authentic Italian tradition.
                       </p>
                     </div>
                     
                     <div>
                       <div style={{
                         backgroundColor: '#1a1a1a',
                         color: '#fff',
                         padding: '30px',
                         borderRadius: '8px'
                       }}>
                         <h4 style={{
                           fontSize: '20px',
                           fontFamily: "'Playfair Display', serif",
                           color: '#D4AF37',
                           marginBottom: '20px',
                           textAlign: 'center'
                         }}>
                           Visit Us
                         </h4>
                         <div style={{fontSize: '15px', lineHeight: '1.8'}}>
                           <div style={{marginBottom: '12px', display: 'flex', alignItems: 'center'}}>
                             <span style={{color: '#D4AF37', marginRight: '10px', fontSize: '16px'}}>📍</span>
                             <span>123 Main Street<br/>Downtown District, NY 10001</span>
                           </div>
                           <div style={{marginBottom: '12px', display: 'flex', alignItems: 'center'}}>
                             <span style={{color: '#D4AF37', marginRight: '10px', fontSize: '16px'}}>📞</span>
                             <span>(555) 123-4567</span>
                           </div>
                           <div style={{marginBottom: '12px', display: 'flex', alignItems: 'center'}}>
                             <span style={{color: '#D4AF37', marginRight: '10px', fontSize: '16px'}}>🕐</span>
                             <span>Tuesday - Sunday<br/>5:00 PM - 10:00 PM</span>
                           </div>
                         </div>
                       </div>
                     </div>
                   </div>
                 </div>
               </div>
             </div>
            
                         <div style={{
               ...styles.chatWidget,
               border: '2px solid #D4AF37',
               boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
             }}>
               <div style={{
                 ...styles.widgetHeader,
                 backgroundColor: '#1a1a1a',
                 borderBottom: '1px solid #D4AF37'
               }}>
                 <span>Bella Vista Assistant</span>
                 <span>×</span>
               </div>
              
              <div style={styles.widgetMessages}>
                {/* Bot Message */}
                <div style={{...styles.message, ...styles.messageBot}}>
                  <div style={{...styles.messageBubble, ...styles.messageBubbleBot}}>
                    Welcome to Bella Vista! I can help you with our menu, reservations, and hours. How can I assist you today?
                  </div>
                  <div style={{...styles.messageTime, ...styles.messageTimeBot}}>
                    2:14 PM
                  </div>
                </div>
                
                {/* User Message */}
                <div style={{...styles.message, ...styles.messageUser}}>
                  <div style={{...styles.messageBubble, ...styles.messageBubbleUser}}>
                    What are your most popular pasta dishes?
                  </div>
                  <div style={{...styles.messageTime, ...styles.messageTimeUser}}>
                    2:15 PM
                  </div>
                </div>
                
                {/* Bot Message */}
                <div style={{...styles.message, ...styles.messageBot}}>
                  <div style={{...styles.messageBubble, ...styles.messageBubbleBot}}>
                    Our signature pasta dishes include Spaghetti Carbonara, Fettuccine Alfredo, and Penne Arrabbiata. We also have daily pasta specials! Would you like to make a reservation?
                  </div>
                  <div style={{...styles.messageTime, ...styles.messageTimeBot}}>
                    2:15 PM
                  </div>
                </div>
                
                {/* User Message */}
                <div style={{...styles.message, ...styles.messageUser}}>
                  <div style={{...styles.messageBubble, ...styles.messageBubbleUser}}>
                    Yes, for 4 people tonight at 7pm
                  </div>
                  <div style={{...styles.messageTime, ...styles.messageTimeUser}}>
                    2:16 PM
                  </div>
                </div>
                
                {/* Bot Message */}
                <div style={{...styles.message, ...styles.messageBot}}>
                  <div style={{...styles.messageBubble, ...styles.messageBubbleBot}}>
                    I'd be happy to help! You can make a reservation by calling us at (555) 123-4567 or through our OpenTable link.
                  </div>
                  <div style={{...styles.messageTime, ...styles.messageTimeBot}}>
                    2:16 PM
                  </div>
                </div>
              </div>
              
              <div style={styles.widgetInputArea}>
                <input style={styles.widgetInput} placeholder="Type a message..." />
                <button style={styles.widgetSendBtn}>
                  ➤
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story Integration Section */}
      <section style={styles.storySection}>
        <div style={styles.storyContent}>
          <div>
            <h2 style={styles.sectionTitle}>Why ByteBot Works</h2>
            <p style={styles.storyText}>
              Many chatbot platforms can be complex and time-consuming to set up, requiring technical knowledge 
              or lengthy configuration processes.
            </p>
            <p style={styles.storyText}>
              ByteBot simplifies this process. Our conversational setup helps you build your chatbot through 
              natural dialogue, making it easier to create a bot that fits your business needs.
            </p>
            <p style={styles.storyText}>
              The result? A chatbot that actually helps your customers and grows your business, without the technical headaches.
            </p>
          </div>
          
          <div style={styles.highlight}>
            <div style={styles.highlightPattern}></div>
            <p style={styles.highlightText}>
              "We don't just build chatbots. We build conversations that convert visitors into customers."
            </p>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section style={styles.process}>
        <div style={styles.processOverlay}></div>
        <div style={styles.processContent}>
          <h2 style={{...styles.sectionTitle, color: '#fff'}}>How It Works</h2>
          <p style={styles.processSubtitle}>
            Three simple steps to transform your customer service with AI
          </p>
          
          <div style={styles.processGrid}>
            <div style={styles.processStep}>
              <div style={styles.stepNumber}>[1]</div>
              <h3 style={styles.stepTitle}>Chat with ByteBot Builder</h3>
              <p style={styles.stepDescription}>
                Have a natural conversation with our ByteBot Builder. Describe your business, your needs, and your goals. No forms, no technical jargon - just talk.
              </p>
            </div>

            <div style={styles.processStep}>
              <div style={styles.stepNumber}>[2]</div>
              <h3 style={styles.stepTitle}>Knowledge Base Creation</h3>
              <p style={styles.stepDescription}>
                Add your content through our simple interface. Upload text, FAQs, or documents (coming soon). Our AI processes everything to understand your business context.
              </p>
            </div>

            <div style={styles.processStep}>
              <div style={styles.stepNumber}>[3]</div>
              <h3 style={styles.stepTitle}>Deploy & Analyze</h3>
              <p style={styles.stepDescription}>
                Launch your chatbot with just a couple clicks. Monitor performance with our AI Analysis Tool and get recommendations to continuously improve customer interactions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={styles.cta}>
        <div style={styles.ctaContent}>
          <h2 style={styles.ctaTitle}>Ready to Build Your Bot?</h2>
          <p style={styles.ctaDescription}>
            Build your intelligent chatbot today<br/>
            and transform your customer experience.
          </p>
          <Link 
            to="/signup" 
            style={styles.ctaButton}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = theme.colors.primaryDark;
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = theme.shadows.xl;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = theme.colors.primary;
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = theme.shadows.lg;
            }}
          >
            [Start Building]
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Features; 